/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import type { Mention, Registre } from '@/types';

import estReconnaissancePrenatale from '../estReconnaissancePrenatale';
import {
  formatCoordonneesRegistre,
  formatDate,
  formatDateNaissance,
  formatLieuActe,
  formatMentions,
  formatParent,
  formatSexe,
} from './formatters';

/**
 * Construit le payload de `POST /numerisation-actes/reconnaissance`, au
 * contrat exact attendu par l'API (implémentée en parallèle côté back sur ce
 * même contrat) :
 * `acte.identifiantSousTypeActe`, `acte.identifiantAuteurReconnaissance`,
 * `acte.dateReconnaissance` / `heureReconnaissance` / `minuteReconnaissance`,
 * `acte.lieuReconnaissance.{codeDepartement,libelleDepartement,codePostal,
 * libelleCommune,libellePays,identifiantCommune,identifiantDepartement,
 * identifiantPays,codeInsee}`, `enfant.naissanceDate` / `enfant.sexe`
 * (nullables, décision 8 « reconnaissance prénatale »), `parent1`/`parent2`
 * (étape 2 réutilisée telle quelle, décision 9 : toujours obligatoires).
 *
 * Fonction pure, testable sans React.
 */
const buildActeReconnaissancePayload = (
  data: Record<string, any>,
  mentions: Mention[],
  selectedRegistre?: Partial<Registre>,
) => {
  const lieuReconnaissance = formatLieuActe(data?.adresseReconnaissance);
  // Champs grisés pour ce sous-type : une valeur saisie avant d'en changer ne
  // doit pas partir pour autant (AD-12845).
  const prenatale = estReconnaissancePrenatale(data?.codeSousTypeActe);

  return {
    acte: {
      ...formatCoordonneesRegistre(selectedRegistre, data?.dateActe),
      numeroActe: data?.numeroActe,
      numeroOrdre: data?.ordreActe,
      acteNumeroComplement: data?.complementActe,
      heureDresseActe: data?.heureActe,
      minuteDresseActe: data?.minuteActe,
      identifiantSousTypeActe: data?.sousTypeActe,
      identifiantAuteurReconnaissance: data?.auteurReconnaissance,
      dateReconnaissance: formatDate(data?.dateReconnaissance),
      heureReconnaissance: data?.heureReconnaissance,
      minuteReconnaissance: data?.minuteReconnaissance,
      ...(!_.isNil(lieuReconnaissance) && { lieuReconnaissance }),
    },
    enfant: {
      nomFamille: data?.nomFamille,
      prenom: data?.prenom,
      prenomsComplementaires: data?.prenomsComplementaires,
      // Nullables (décision 8) : la reconnaissance prénatale (sous-type `N`)
      // se fait avant que l'enfant ne naisse, donc avant que sa date de
      // naissance / son sexe ne soient connus.
      ...(!prenatale && {
        naissanceDate: formatDateNaissance(data),
        naissanceHeure: data?.heureNaissance,
        naissanceMinute: data?.minuteNaissance,
      }),
      ...(!_.isNil(formatSexe(data?.sexe)) && { sexe: formatSexe(data?.sexe) }),
    },
    // Sur une reconnaissance, une filiation non renseignée désigne un parent
    // *inconnu* (et non décédé comme sur une naissance) : le back crée alors
    // l'individu sans nom, sans toucher à `ai_vivant`.
    parent1: formatParent(
      data?.parent1,
      data?.filiationInconnueParent1,
      'inconnu',
    ),
    parent2: formatParent(
      data?.parent2,
      data?.filiationInconnueParent2,
      'inconnu',
    ),
    ...(!_.isEmpty(mentions) && { mentions: formatMentions(mentions) }),
  };
};

export default buildActeReconnaissancePayload;
